import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { CsPage, NavController } from 'providers/nav-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { EditTrainingGoalPageParams, EditTrainingGoalPageResult } from './edit-training-goal-interface';
import { DeclaredState, isUsersTrainingGoalGrowth } from '@cf-shared/data-model/user-state';
import { ImmutableState, FitnessState } from '@cf-shared/data-model/user-state';
import { TrainingGoal } from '@cf-shared/enums';
import { UserDb } from 'services/user-db.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LL } from 'i18n/i18n.service';
import { IIntakeFormPage, IntakeStepUrl } from 'pages/intake-form/intake-form-interface';
import { DatabaseWrapper , DocWatcher } from 'services/database-wrapper';
import { UnsavedChangesGuard, UnsavedChangesGuardProvider, CanLeaveResult } from 'providers/unsaved-changes-guard/unsaved-changes-guard';
import { LoggingService } from 'services/logging.service';

enum Method {
  GrowthMindset = 'growthMindset',
  FixedTarget = 'fixedTarget'
}

enum TargetUnit {
  Weight = 'weight',
  Bodyfat = 'bodyfat'
}

@Component({
  selector: 'page-edit-training-goal',
  templateUrl: 'edit-training-goal-page.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 480, height: 'auto' }) }]
})
export class EditTrainingGoalPage extends CsPage<EditTrainingGoalPageParams, EditTrainingGoalPageResult> {
  protected LL = LL;
  protected loading = true;

  protected declaredState: DeclaredState;
  protected immutableState: ImmutableState;
  protected fitnessState: FitnessState;

  /**
   * Everything on this page the user can change, held in ONE object.
   *
   * The unsaved-changes guard watches objects, not loose fields: it keeps a copy
   * of what it was handed when the page loaded and compares against it later. So
   * the four answers live together here rather than as four properties on the
   * page.
   */
  protected form: {
    method: Method;
    targetUnit: TargetUnit;
    goalWeight_kg: number;
    goalBodyfat_pc: number;
  } = {
    method: Method.GrowthMindset,
    targetUnit: TargetUnit.Weight,
    goalWeight_kg: undefined,
    goalBodyfat_pc: undefined,
  };

  private docWatcher: DocWatcher;
  private unsavedChangesGuard: UnsavedChangesGuard<EditTrainingGoalPageResult>;
  /**
   * True once the goal has actually been written.
   *
   * The page answers its caller with a `modified` flag, and the caller reloads
   * its goal data when it is true. The guard hands a result back on two paths —
   * when the user picks Save, and when nothing was changed at all — so a flat
   * `true` would claim a modification after a Cancel or an untouched Back. Only
   * the Save path runs the saver, which is where this is set.
   */
  private goalWasSaved = false;
  Method = Method;
  TargetUnit = TargetUnit;

  constructor(
    public navCtrl: NavController,
    private userDb: UserDb,
    private alertCtrl: AlertController,
    private unsavedChangesGuardProvider: UnsavedChangesGuardProvider,
    private logging: LoggingService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  async ionViewWillEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cdr.markForCheck();
      });
    }

    await this.loadDataAsync();
  }

  private async loadDataAsync() {
    this.loading = true;
    this.cdr.markForCheck();

    this.declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);
    this.immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
    this.fitnessState = await this.userDb.loadFitnessStateAsync(this.docWatcher);

    this.initFormState();

    // Build the guard only now, with the form holding exactly what was stored.
    // Anything the user changes from here on counts as unsaved work, and the
    // guard asks about it before the page closes (#5058).
    this.unsavedChangesGuard = this.unsavedChangesGuardProvider.createGuard<EditTrainingGoalPageResult>(
      [this.form],
      async () => { await this.saveFromLeaveDialogAsync(); },
      { getResult: (): EditTrainingGoalPageResult => ({ modified: this.goalWasSaved }) },
    );

    this.loading = false;
    this.cdr.markForCheck();
  }

  /** Fill the form from what is stored. Also used to undo edits on Cancel. */
  private initFormState() {
    const ds = this.declaredState;

    // determine method based on whether targets are set
    if (ds.targetBodyfat_pc != null || ds.targetBodyWeight_kg != null) {
      this.form.method = Method.FixedTarget;
      this.form.targetUnit = ds.targetBodyfat_pc != null ? TargetUnit.Bodyfat : TargetUnit.Weight;
    } else {
      this.form.method = Method.GrowthMindset;
      this.form.targetUnit = TargetUnit.Weight;
    }

    // load existing values
    this.form.goalWeight_kg = ds.targetBodyWeight_kg;
    this.form.goalBodyfat_pc = ds.targetBodyfat_pc;
  }

  get primaryGoalText(): string {
    if (this.isMuscleGrowth) {
      return LL.editTrainingGoal.muscleGrowth();
    }
    return LL.editTrainingGoal.fatLoss();
  }

  get isMuscleGrowth(): boolean {
    if (!this.declaredState || !this.immutableState || !this.fitnessState) {
      return false;
    }
    const tg = this.declaredState.trainingGoalGrowth;
    // if explicit muscle growth
    if (tg === true || tg === TrainingGoal.MuscleGrowth) {
      return true;
    }
    // if explicit fat loss
    if (tg === false || tg === TrainingGoal.FatLoss) {
      return false;
    }
    // if automatic, resolve based on body fat
    return isUsersTrainingGoalGrowth(
      tg,
      this.immutableState.gender,
      this.fitnessState.bfData?.bf_pc ?? 15
    );
  }

  onMethodChange() {
    this.cdr.markForCheck();
  }

  async handleUpdatePrimaryGoal(event: MouseEvent) {
    event.stopPropagation();

    const result = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.editTrainingGoal.updateAlert.title(),
      message: LL.editTrainingGoal.updateAlert.message(),
      buttons: [
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
          result: false
        },
        {
          text: LL.editTrainingGoal.updateAlert.updateGoal(),
          role: AlertButtonRole.Primary,
          result: true
        }
      ]
    });

    if (result) {
      await this.navCtrl.pushAsync(IIntakeFormPage, { mode: 'change', tab: IntakeStepUrl.Goals });
    }
  }

  /** Write the form's answers into the stored goal. The guard calls this when
   *  the user picks Save in the leave dialog; the Save button calls it too. */
  private async applyFormToStoredGoalAsync() {
    const declaredState = await this.userDb.loadDeclaredStateAsync(this.docWatcher);

    if (this.form.method === Method.GrowthMindset) {
      // clear targets
      declaredState.targetBodyfat_pc = undefined;
      declaredState.targetBodyWeight_kg = undefined;
    } else {
      // fixed target
      if (this.isMuscleGrowth || this.form.targetUnit === TargetUnit.Weight) {
        declaredState.targetBodyWeight_kg = this.form.goalWeight_kg;
        declaredState.targetBodyfat_pc = undefined;
      } else {
        declaredState.targetBodyfat_pc = this.form.goalBodyfat_pc;
        declaredState.targetBodyWeight_kg = undefined;
      }
    }

    await this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
  }

  /**
   * Save on the way out, when the user picks Save in the leave dialog.
   *
   * `cs-nav` runs the guard inside a try/catch that turns any throw into "you
   * may not leave" plus a console line nobody sees in production: no telemetry,
   * no message, and a page that just refuses to close. So report the failure
   * before letting it travel on. The rethrow keeps the page open with the user's
   * answers still in it, which is right — it just must not be silent.
   */
  private async saveFromLeaveDialogAsync() {
    try {
      await this.applyFormToStoredGoalAsync();
      this.goalWasSaved = true;
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.unsavedChanges.errors.saveFailed());
      throw err;
    }
  }

  async handleSave() {
    // The Save button sits outside the page's loading area, so it is clickable
    // while the goal is still being read. Do nothing until there is something to
    // save — the alternative is an unhandled TypeError on the guard.
    if (!this.unsavedChangesGuard) return;
    // saveAndExitAsync saves and then closes the page, handing the caller the
    // result the guard would have handed it.
    await this.unsavedChangesGuard.saveAndExitAsync();
  }

  /**
   * Runs before the page closes, whichever way the user leaves: the browser back
   * button, Android's hardware back button, a swipe back, or the Cancel button.
   * Asks whether to save when the form no longer matches what was stored (#5058).
   */
  async ionViewCanLeave(): Promise<CanLeaveResult<EditTrainingGoalPageResult>> {
    // the guard is built once the goal has loaded; before that there is nothing
    // the user could have changed, so let the page go
    if (!this.unsavedChangesGuard) return true;
    return this.unsavedChangesGuard.confirmLeaveAsync();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }

  handleCancel(event: Event) {
    lockClick(event, this.handleCancelAsync());
  }

  /**
   * Cancel means "throw my edits away", so put the form back to what is stored
   * before closing. The guard then finds nothing changed and stays quiet —
   * asking "apply changes?" after the user pressed Cancel would be absurd.
   */
  private async handleCancelAsync() {
    this.initFormState();
    await this.navCtrl.popAsync();
  }
}
